import React, { useState, useEffect, useRef } from 'react';
import css from './ParentDashboard.module.css';
import studentModalCss from './StudentDashboard.module.css';
import teacherModalCss from './TeacherDashboard.module.css';
import { renderMarkdownNote } from '../../utils/client-functions';
import { compressImageIfNeeded, validateImageFile } from '../../utils/imageUtils';

// Reuse the same grading UI building blocks as StudentDashboard's own results view
import {
  GlassCard, StatGrid, StatCard,
  ScoreCircleWrapper, ScoreText,
  ProgressBarTrack, ProgressBarFill, Badge,
} from '../../styled/InsightNTutor';
import {
  FaTrophy, FaChartLine, FaHourglass, FaUsers, FaChartPie,
  FaCheckCircle, FaTimesCircle, FaChevronDown, FaChevronUp, FaRobot, FaRegBookmark,
  FaBookOpen, FaVideo,
} from 'react-icons/fa';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartIcon from '@mui/icons-material/BarChart';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import CareerPathSummaryCard from './CareerPathSummaryCard';
import { RetakeBadge } from './RetakeControls';
import ReportCardLibrary from './ReportCardLibrary';

function fmtDateShort(d) {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-NG', { month: 'short', day: 'numeric' });
}
function fmtDateTime(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('en-NG', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function termLabel(t) {
  return t === 'first' ? 'First' : t === 'second' ? 'Second' : t === 'third' ? 'Third' : t;
}

function hasChildPhoto(profileImg) {
  const value = String(profileImg || '').trim();
  return Boolean(value && value !== '/uploads/avatar.png');
}

function childInitials(firstName = '', lastName = '') {
  return `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase() || '?';
}

// ─── Curriculum (expandable) ──────────────────────────────────────────────────
// Parent-facing, read-only mirror of StudentDashboard's CurriculumSegment —
// same card grid / topic-chip styling (reused via studentModalCss), collapsed
// by default behind an expand/collapse header so it doesn't crowd the page.
function ChildCurriculumSegment({ membershipId, student, fetchFn }) {
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [subjects, setSubjects] = useState([]);
  const [className, setClassName] = useState('');
  const [viewSubject, setViewSubject] = useState(null);

  useEffect(() => {
    if (!open || loaded || !membershipId) return;
    let alive = true;
    setLoading(true); setError('');
    fetchFn(`/parent/children/${membershipId}/curriculum`).then(r => r.json()).then(data => {
      if (!alive) return;
      setSubjects(data.subjects || []);
      setClassName(data.className || '');
      setLoaded(true);
      setLoading(false);
    }).catch(() => { if (alive) { setError('Could not load curriculum.'); setLoading(false); } });
    return () => { alive = false; };
  }, [open, loaded, membershipId, fetchFn]);

  const uploadedCount = subjects.filter(s => !!s.curriculum?.key).length;

  return (
    <div className={css.card}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none',
          border: 'none', cursor: 'pointer', padding: 0, textAlign: 'left',
        }}
      >
        <MenuBookIcon fontSize="small" style={{ color: '#1a8a92' }} />
        <h3 style={{ margin: 0, fontSize: '0.92rem', color: '#0c1e28', flex: 1 }}>
          {student?.firstName || 'Child'}'s Curriculum{className ? ` — ${className}` : ''}
        </h3>
        {loaded && (
          <span style={{
            fontSize: '0.72rem', fontWeight: 700, color: '#1a8a92', background: 'rgba(95,190,197,0.14)',
            borderRadius: 20, padding: '2px 10px',
          }}>
            {uploadedCount}/{subjects.length} uploaded
          </span>
        )}
        {open ? <FaChevronUp style={{ color: '#90a4ae', fontSize: '0.85rem' }} /> : <FaChevronDown style={{ color: '#90a4ae', fontSize: '0.85rem' }} />}
      </button>

      <div
        style={{
          maxHeight: open ? 3000 : 0,
          overflow: 'hidden',
          transition: 'max-height 0.35s ease',
        }}
      >
        <div style={{ paddingTop: 16 }}>
          <p style={{ fontSize: '0.82rem', color: '#78909c', margin: '0 0 16px' }}>
            Curriculum documents uploaded by teachers for each of your child's subjects.
          </p>

          {error && (
            <div style={{ background: '#fdecea', color: '#c62828', border: '1px solid #f5c6cb', borderRadius: 8, padding: '10px 14px', fontSize: '0.85rem', marginBottom: 14 }}>
              {error}
            </div>
          )}

          {loading ? (
            <div className={studentModalCss.curriculumGrid}>
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className={studentModalCss.curriculumCard}>
                  <div className={studentModalCss.skel} style={{ height: 16, width: '60%', marginBottom: 10 }} />
                  <div className={studentModalCss.skel} style={{ height: 12, width: '90%', marginBottom: 6 }} />
                  <div className={studentModalCss.skel} style={{ height: 12, width: '70%' }} />
                </div>
              ))}
            </div>
          ) : loaded && subjects.length === 0 ? (
            <div className={studentModalCss.emptyState}>
              <MenuBookIcon style={{ fontSize: '2rem', color: '#c0ccda' }} />
              <p className={studentModalCss.emptyTitle}>No curriculum available yet</p>
              <p className={studentModalCss.emptyDesc}>Curriculum will appear here once teachers upload it and make it visible.</p>
            </div>
          ) : loaded ? (
            <div className={studentModalCss.curriculumGrid}>
              {subjects.map(sub => {
                const hasCurriculum = !!(sub.curriculum?.key);
                return (
                  <div key={sub._id} className={`${studentModalCss.curriculumCard} ${hasCurriculum ? studentModalCss.curriculumCardFilled : ''}`}>
                    <div className={studentModalCss.curriculumCardHeader}>
                      <MenuBookIcon fontSize="small" style={{ color: hasCurriculum ? '#1a6e73' : '#90a4ae' }} />
                      <span className={studentModalCss.curriculumCardName}>{sub.name}</span>
                      {hasCurriculum && <span className={studentModalCss.curriculumBadge}>Uploaded</span>}
                    </div>

                    {hasCurriculum ? (
                      <>
                        <div className={studentModalCss.curriculumCardMeta}>
                          <span>{sub.curriculum.originalName}</span>
                          <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>
                            {sub.curriculum.uploadedAt ? new Date(sub.curriculum.uploadedAt).toLocaleDateString() : ''}
                          </span>
                        </div>

                        {sub.curriculum.topics?.length > 0 && (
                          <div className={studentModalCss.curriculumTopics}>
                            {sub.curriculum.topics.slice(0, 6).map((t, i) => (
                              <span key={i} className={studentModalCss.curriculumTopicChip}>{t}</span>
                            ))}
                            {sub.curriculum.topics.length > 6 && (
                              <span className={studentModalCss.curriculumTopicMore}>+{sub.curriculum.topics.length - 6} more</span>
                            )}
                          </div>
                        )}

                        <div className={studentModalCss.curriculumCardActions}>
                          <button className={studentModalCss.curriculumActionBtn} onClick={() => setViewSubject(sub)} title="View details">
                            <VisibilityIcon fontSize="small" /> View
                          </button>
                          <a className={studentModalCss.curriculumActionBtn} href={sub.curriculum.url} download={sub.curriculum.originalName} title="Download">
                            <DownloadIcon fontSize="small" /> Download
                          </a>
                        </div>
                      </>
                    ) : (
                      <div className={studentModalCss.curriculumUploadArea}>
                        <p className={studentModalCss.curriculumUploadHint}>No curriculum uploaded yet for this subject.</p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : null}
        </div>
      </div>

      {/* View / detail modal */}
      {viewSubject && (
        <div className={studentModalCss.spModalOverlay} onClick={() => setViewSubject(null)}>
          <div className={`${studentModalCss.spModalBox} BubblePopIn`} style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
            <div className={studentModalCss.spModalHeader}>
              <div>
                <h3 className={studentModalCss.spModalTitle}>{viewSubject.name} — Curriculum</h3>
                <p className={studentModalCss.spModalSub}>{className}</p>
              </div>
              <button className={studentModalCss.spModalClose} onClick={() => setViewSubject(null)}>✕</button>
            </div>
            <div style={{ padding: '18px 24px 24px' }}>
              <p style={{ fontSize: '0.82rem', color: '#546e7a', marginBottom: 8 }}>
                <strong>File:</strong> {viewSubject.curriculum?.originalName} &nbsp;·&nbsp;
                Uploaded {viewSubject.curriculum?.uploadedAt ? new Date(viewSubject.curriculum.uploadedAt).toLocaleDateString() : '—'}
              </p>
              {viewSubject.curriculum?.summary && (
                <>
                  <h4 style={{ marginBottom: 6, color: '#263238', fontSize: '0.85rem' }}>Summary</h4>
                  <p style={{ fontSize: '0.83rem', color: '#37474f', lineHeight: 1.6, marginBottom: 16 }}>{viewSubject.curriculum.summary}</p>
                </>
              )}
              {viewSubject.curriculum?.topics?.length > 0 && (
                <>
                  <h4 style={{ marginBottom: 8, color: '#263238', fontSize: '0.85rem' }}>Topics ({viewSubject.curriculum.topics.length})</h4>
                  <div className={studentModalCss.curriculumTopics} style={{ flexWrap: 'wrap' }}>
                    {viewSubject.curriculum.topics.map((t, i) => (
                      <span key={i} className={studentModalCss.curriculumTopicChip}>{t}</span>
                    ))}
                  </div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 20 }}>
                <a className={studentModalCss.primaryBtn} href={viewSubject.curriculum?.url} download={viewSubject.curriculum?.originalName} style={{ textDecoration: 'none' }}>
                  <DownloadIcon fontSize="small" /> Download File
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Grading detail (topic breakdown / question analysis / comparative) ──────
// A read-only, parent-facing trim of StudentDashboard's GradingDetails — no
// study plan / AI tutor actions since those are student self-service tools.
function ChildGradingDetails({ grading, exam, isMultiSubject, subjectBreakdown = [], activeSubjectIdx = 'all', setActiveSubjectIdx = () => {} }) {
  const isMulti = isMultiSubject && subjectBreakdown.length > 1;
  const currentSubjectData = isMulti && activeSubjectIdx !== 'all'
    ? (subjectBreakdown.find(s => s.subjectIdx === activeSubjectIdx) || null)
    : null;

  // Effective grading view — combined ("All Subjects") or the selected subject's
  // own breakdown. Every stat/section below reads from `eg`.
  const eg = currentSubjectData ? {
    score:            currentSubjectData.score,
    pass:             currentSubjectData.passed,
    percentage:       currentSubjectData.percentage,
    topicBreakdown:   currentSubjectData.topicBreakdown,
    comparative:      currentSubjectData.comparative,
    questionAnalysis: currentSubjectData.questionAnalysis,
    errors:           currentSubjectData.errors,
    feedback:         grading?.feedback,
  } : (grading || {});

  const percentage = eg.percentage || exam?.percentage || 0;
  const scoreColor = eg.pass ? '#43AA8B' : '#DC143C';
  const mainColor  = '#43AA8B';
  const totalScore = currentSubjectData ? (currentSubjectData.totalScore ?? null) : (exam?.totalScore ?? null);

  const [isQuestionExpanded, setIsQuestionExpanded] = useState(false);
  const [isErrorExpanded,    setIsErrorExpanded]    = useState(false);
  const hasManyQuestions = (eg.questionAnalysis?.length || 0) > 3;
  const hasManyErrors    = (eg.errors?.length || 0) > 3;
  const COLLAPSED_Q_HEIGHT = 280;
  const COLLAPSED_ERR_HEIGHT = 230;

  const totalTime = (eg.questionAnalysis || []).reduce((acc, q) => acc + (parseInt(q.time) || 0), 0);
  const peerDiff = eg.comparative
    ? (percentage - (eg.comparative.average || 0)).toFixed(1)
    : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      {/* ── Subject pills (multi-subject exams only) ── */}
      {isMulti && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          <button
            type="button"
            onClick={() => setActiveSubjectIdx('all')}
            style={{
              padding: '6px 14px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700,
              cursor: 'pointer', whiteSpace: 'nowrap',
              border: activeSubjectIdx === 'all' ? '1px solid #5fbec5' : '1px solid rgba(0,0,0,0.12)',
              background: activeSubjectIdx === 'all' ? '#5fbec5' : '#fff',
              color: activeSubjectIdx === 'all' ? '#fff' : '#455a64',
            }}
          >
            All Subjects
          </button>
          {subjectBreakdown.map(s => (
            <button
              key={s.subjectIdx}
              type="button"
              onClick={() => setActiveSubjectIdx(s.subjectIdx)}
              style={{
                padding: '6px 14px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700,
                cursor: 'pointer', whiteSpace: 'nowrap',
                border: activeSubjectIdx === s.subjectIdx ? '1px solid #5fbec5' : '1px solid rgba(0,0,0,0.12)',
                background: activeSubjectIdx === s.subjectIdx ? '#5fbec5' : '#fff',
                color: activeSubjectIdx === s.subjectIdx ? '#fff' : '#455a64',
              }}
            >
              {s.subjectName}
            </button>
          ))}
        </div>
      )}

      <StatGrid>
        <StatCard iconColor={scoreColor}>
          <div className="icon"><FaTrophy /></div>
          <ScoreCircleWrapper percentage={percentage} color={scoreColor}>
            <svg width="150" height="150">
              <circle className="bg"       cx="75" cy="75" r="70" />
              <circle className="progress" cx="75" cy="75" r="70" />
            </svg>
            <ScoreText>
              <span className="value" style={{ color: scoreColor }}>{eg.score}</span>
              <span className="label">Total Score</span>
            </ScoreText>
          </ScoreCircleWrapper>
          <div style={{ marginTop: '0.75rem' }}>
            <Badge variant={eg.pass ? 'success' : 'danger'}>{eg.pass ? 'Passed' : 'Failed'}</Badge>
          </div>
        </StatCard>

        <StatCard iconColor={mainColor}>
          <div className="icon"><FaChartLine /></div>
          <div className="value">{percentage}%</div>
          <div className="label">Accuracy Rate</div>
          <div style={{ marginTop: 'auto', width: '100%' }}>
            <ProgressBarTrack height="6px"><ProgressBarFill progress={percentage} color={mainColor} /></ProgressBarTrack>
          </div>
        </StatCard>

        <StatCard iconColor={mainColor}>
          <div className="icon"><FaHourglass /></div>
          <div className="value">{totalTime}s</div>
          <div className="label">Total Time Spent</div>
        </StatCard>

        <StatCard iconColor={mainColor}>
          <div className="icon"><FaUsers /></div>
          <div className="value">{peerDiff !== null ? `${peerDiff > 0 ? '+' : ''}${peerDiff}%` : '—'}</div>
          <div className="label">vs. Peer Average</div>
        </StatCard>

        {totalScore != null && (
          <StatCard iconColor="#6d4c41">
            <div className="icon"><FaTrophy style={{ color: '#6d4c41' }} /></div>
            <div className="value" style={{ color: '#6d4c41' }}>{totalScore}</div>
            <div className="label">Total Attainable</div>
          </StatCard>
        )}
      </StatGrid>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>
        {(eg.topicBreakdown || []).length > 0 && (
          <GlassCard style={{ flex: '1 1 320px' }}>
            <h6 style={{ margin: '0 0 1.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaChartPie color="#43AA8B" /> Topic Performance{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}
            </h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {eg.topicBreakdown.map((t, i) => {
                const pct = t.total > 0 ? Math.round((t.correct / t.total) * 100) : 0;
                return (
                  <div key={i}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                      <span style={{ fontSize: '0.88rem', fontWeight: 600 }}>{t.topic}</span>
                      <span style={{ fontSize: '0.88rem', opacity: 0.8 }}>{t.correct}/{t.total} ({pct}%)</span>
                    </div>
                    <ProgressBarTrack><ProgressBarFill progress={pct} /></ProgressBarTrack>
                  </div>
                );
              })}
            </div>
          </GlassCard>
        )}

        {eg.comparative && (
          <GlassCard style={{ flex: '1 1 240px' }}>
            <h6 style={{ margin: '0 0 1.25rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
              <FaUsers color="#43AA8B" /> Comparative Analysis{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}
            </h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', paddingTop: '0.5rem' }}>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Child's Performance</span>
                  <span style={{ color: '#43AA8B', fontWeight: 700 }}>{percentage}%</span>
                </div>
                <ProgressBarTrack height="12px"><ProgressBarFill progress={percentage} color="#43AA8B" /></ProgressBarTrack>
              </div>
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                  <span style={{ fontWeight: 600, fontSize: '0.9rem' }}>Peer Average</span>
                  <span style={{ color: '#FF6B6B', fontWeight: 700 }}>{eg.comparative.average}%</span>
                </div>
                <ProgressBarTrack height="12px"><ProgressBarFill progress={eg.comparative.average} color="#FF6B6B" /></ProgressBarTrack>
              </div>
            </div>
          </GlassCard>
        )}
      </div>

      {(eg.questionAnalysis || []).length > 0 && (
        <GlassCard>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1.25rem' }}>
            <h6 style={{ margin: 0, fontWeight: 700 }}>Question-Level Analysis{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}</h6>
            {hasManyQuestions && (
              <button
                type="button"
                onClick={() => setIsQuestionExpanded(p => !p)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999,
                  border: '1px solid rgba(95,190,197,0.6)',
                  background: 'linear-gradient(90deg, rgba(95,190,197,0.1), rgba(162,89,255,0.14))',
                  color: '#0f3135', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                }}
              >
                <span>See all</span>
                {isQuestionExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </button>
            )}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{
              maxHeight: hasManyQuestions && !isQuestionExpanded ? COLLAPSED_Q_HEIGHT : 'none',
              overflow: hasManyQuestions && !isQuestionExpanded ? 'hidden' : 'visible',
              transition: 'max-height 0.35s ease',
            }}>
              <table className={css.table} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
                    {['#', 'Question', 'Result', 'Selected', 'Correct', 'Score', 'Time', 'Topic'].map(h => (
                      <th key={h} style={{ textAlign: 'left', padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 700, color: '#607d8b' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {eg.questionAnalysis.map((q, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid rgba(0,0,0,0.03)' }}>
                      <td style={{ padding: '10px 12px', fontWeight: 700 }}>{q.number}</td>
                      <td style={{ padding: '10px 12px', minWidth: 180, maxWidth: 300, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {q.question || q.question_text || '—'}
                      </td>
                      <td style={{ padding: '10px 12px' }}>
                        {q.correct ? <FaCheckCircle color="#43AA8B" size={16} /> : <FaTimesCircle color="#FF6B6B" size={16} />}
                      </td>
                      <td style={{ padding: '10px 12px', color: q.correct ? '#43AA8B' : '#FF6B6B', fontWeight: 600 }}>{q.chose || '—'}</td>
                      <td style={{ padding: '10px 12px', color: '#43AA8B', fontWeight: 600 }}>{q.right || '—'}</td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', fontWeight: 600 }}>
                        {q.scoreAwarded != null && q.maxScore != null ? `${q.scoreAwarded}/${q.maxScore}` : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', whiteSpace: 'nowrap' }}>{q.time ?? '—'}s</td>
                      <td style={{ padding: '10px 12px' }}>
                        {q.topic ? (
                          <Badge variant="success" style={{ background: 'rgba(62,143,147,0.1)', color: '#3E8F93', border: 'none', fontSize: '0.7rem' }}>
                            {q.topic}
                          </Badge>
                        ) : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </GlassCard>
      )}

      {/* ── Error Analysis ── */}
      <GlassCard>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', marginBottom: '1rem' }}>
          <h6 style={{ margin: 0, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8, color: '#FF6B6B' }}>
            <FaRegBookmark /> Error Analysis{currentSubjectData ? ` — ${currentSubjectData.subjectName}` : ''}
          </h6>
          {hasManyErrors && (
            <button
              type="button"
              onClick={() => setIsErrorExpanded(p => !p)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '6px 14px', borderRadius: 999,
                border: '1px solid rgba(95,190,197,0.6)',
                background: 'linear-gradient(90deg, rgba(95,190,197,0.1), rgba(162,89,255,0.14))',
                color: '#0f3135', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              <span>See all</span>
              {isErrorExpanded ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
            </button>
          )}
        </div>
        {(eg.errors || []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1rem' }}>
            <div style={{ fontSize: '2rem' }}>🎉</div>
            <p style={{ margin: '8px 0 0', color: '#43AA8B', fontWeight: 600 }}>No errors found.</p>
          </div>
        ) : (
          <div style={{
            maxHeight: hasManyErrors && !isErrorExpanded ? COLLAPSED_ERR_HEIGHT : 'none',
            overflow: hasManyErrors && !isErrorExpanded ? 'hidden' : 'visible',
            transition: 'max-height 0.35s ease',
          }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {eg.errors.map((e, i) => (
                <div key={i} style={{ padding: '10px 14px', borderRadius: 12, background: 'rgba(255,107,107,0.05)', border: '1px solid rgba(255,107,107,0.12)' }}>
                  <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: 4 }}>Question {e.question}</div>
                  <div style={{ fontSize: '0.83rem', color: '#555' }}>{e.mistake}</div>
                  <div style={{ fontSize: '0.79rem', fontStyle: 'italic', marginTop: 4, color: '#777' }}>{e.explanation}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </GlassCard>

      {grading.feedback && (
        <GlassCard style={{ borderLeft: '4px solid #A259FF' }}>
          <h6 style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '0 0 0.75rem', fontWeight: 700 }}>
            <FaRobot color="#A259FF" /> BubbleAI Feedback Summary
          </h6>
          <p style={{ margin: 0, fontSize: '0.88rem', lineHeight: '1.65', color: '#444' }}>{grading.feedback}</p>
        </GlassCard>
      )}
    </div>
  );
}

// ─── Read-only study plan viewer for parents (no task toggling / AI actions) ──
function ChildStudyPlanView({ studyPlan, progress, exam, membershipId, fetchFn }) {
  const schedule = studyPlan?.schedule || [];
  const [subjectIdx, setSubjectIdx] = useState('all');
  const [detail, setDetail] = useState({ type: null, task: null, loading: false, content: null, error: '', fullShown: false });
  const allTasks = schedule.flatMap(day => day.tasks || []);
  const subjects = Array.from(new Map(
    allTasks
      .filter(task => typeof task.subjectIdx === 'number')
      .map(task => [task.subjectIdx, task.subjectName || `Subject ${task.subjectIdx + 1}`])
  ).entries()).sort((a, b) => a[0] - b[0]);
  const isMultiPlan = exam?.isMultiSubject && subjects.length > 1;
  const selectedSubjectIdx = isMultiPlan && subjectIdx === 'all' ? subjects[0][0] : subjectIdx;
  const visibleTasks = isMultiPlan
    ? allTasks.filter(task => task.subjectIdx === selectedSubjectIdx)
    : allTasks;

  const openTask = async task => {
    const resourceType = (task.resource?.resourceType || task.resource?.type || '').toLowerCase();
    const type = resourceType.includes('video') ? 'video' : resourceType.includes('mock') ? 'mock' : 'note';
    setDetail({ type, task, loading: true, content: null, error: '', fullShown: false });
    const title = task.resource?.title || task.title || '';
    let url;
    if (type === 'video') {
      url = `/parent/children/${membershipId}/study-plan/video?q=${encodeURIComponent(title)}`;
    } else if (type === 'mock') {
      url = `/parent/children/${membershipId}/study-plan/mock-result?schoolExamId=${exam.schoolExamId}&topic=${encodeURIComponent(title)}`;
    } else {
      url = `/parent/children/${membershipId}/study-plan/note?examObjectId=${exam.examId}&title=${encodeURIComponent(title)}&resourceTitle=${encodeURIComponent(task.resource?.title || '')}`;
    }
    try {
      const response = await fetchFn(url);
      const data = await response.json();
      setDetail(current => ({
        ...current,
        loading: false,
        content: type === 'video' ? data.video : type === 'mock' ? data.result : data.note,
        error: response.ok ? '' : (data.error || data.message || 'Failed to load task details'),
      }));
    } catch {
      setDetail(current => ({ ...current, loading: false, error: 'Failed to load task details' }));
    }
  };

  return (
    <div>
      {isMultiPlan && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 14 }}>
          {subjects.map(([idx, name]) => (
            <button
              key={idx}
              type="button"
              onClick={() => setSubjectIdx(idx)}
              style={{
                padding: '6px 14px', borderRadius: 20, fontSize: '0.78rem', fontWeight: 700, cursor: 'pointer',
                border: `1.5px solid ${selectedSubjectIdx === idx ? '#3e8f93' : '#d0dce0'}`,
                background: selectedSubjectIdx === idx ? '#3e8f93' : '#fff', color: selectedSubjectIdx === idx ? '#fff' : '#455a64',
              }}
            >
              {name}
            </button>
          ))}
        </div>
      )}
      {progress && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <span style={{ fontSize: '0.8rem', color: '#90a4ae' }}>
            {visibleTasks.filter(task => task.done).length} / {visibleTasks.length} tasks done
          </span>
          <div className={css.progressTrack} style={{ width: 140 }}>
            <div className={css.progressFill} style={{ width: `${visibleTasks.length ? Math.round((visibleTasks.filter(task => task.done).length / visibleTasks.length) * 100) : 0}%` }} />
          </div>
        </div>
      )}
      {schedule.map((day, di) => {
        const tasks = (day.tasks || []).filter(task => !isMultiPlan || task.subjectIdx === selectedSubjectIdx);
        if (tasks.length === 0) return null;
        return (
        <div key={day.date || di} style={{ marginBottom: 16 }}>
          <p style={{ margin: '0 0 8px', fontSize: '0.8rem', fontWeight: 700, color: '#607d8b' }}>{day.date}</p>
          {tasks.map((task, ti) => (
            <div
              key={ti}
              onClick={() => openTask(task)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                borderRadius: 10, background: task.done ? 'rgba(67,170,139,0.08)' : '#f8fafc',
                border: '1px solid rgba(0,0,0,0.05)', marginBottom: 6, cursor: 'pointer',
              }}
            >
              <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                width: 20, height: 20, borderRadius: '50%',
                background: task.done ? '#43AA8B' : '#e0e7ec', color: '#fff', fontSize: '0.7rem', flexShrink: 0,
              }}>
                {task.done ? '✓' : ''}
              </span>
              <div style={{ flex: 1 }}>
                <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#0c1e28' }}>{task.title}</p>
                <p style={{ margin: 0, fontSize: '0.75rem', color: '#90a4ae' }}>
                  {task.timeAllocation} min
                  {task.importance >= 4 && <span style={{ marginLeft: 6, color: '#f59e0b' }}>★ High priority</span>}
                </p>
              </div>
              <span style={{ fontSize: '0.75rem', color: '#3e8f93', fontWeight: 700 }}>View</span>
            </div>
          ))}
        </div>
        );
      })}

      {detail.type && detail.type !== 'mock' && (
        <div className={studentModalCss.spModalOverlay} onClick={() => setDetail({ type: null, task: null, loading: false, content: null, error: '', fullShown: false })}>
          <div className={`${studentModalCss.spModalBox} BubblePopIn`} style={{ maxWidth: detail.type === 'note' ? 620 : 700 }} onClick={event => event.stopPropagation()}>
            <div className={studentModalCss.spModalHeader}>
              <div>
                <h3 className={studentModalCss.spModalTitle}>
                  {detail.type === 'note'
                    ? <FaBookOpen style={{ marginRight: 8, color: '#5fbec5' }} />
                    : <FaVideo style={{ marginRight: 8, color: '#5fbec5' }} />}
                  {detail.task?.resource?.title || detail.task?.title || (detail.type === 'note' ? 'Study Note' : 'Study Video')}
                </h3>
                <p className={studentModalCss.spModalSub}>{detail.type === 'note' ? 'AI-generated notes for this topic' : 'Recommended study video'}</p>
              </div>
              <button className={studentModalCss.spModalClose} onClick={() => setDetail({ type: null, task: null, loading: false, content: null, error: '', fullShown: false })}>✕</button>
            </div>

            {detail.type === 'note' ? (
              <div className={studentModalCss.spNoteBody}>
                {detail.loading ? (
                  <div className={studentModalCss.spNoteLoading}><p>Loading notes…</p></div>
                ) : detail.error ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#dc2626' }}>{detail.error}</div>
                ) : detail.content ? (
                  <>
                    <div
                      className={studentModalCss.spNoteContent}
                      style={{ maxHeight: detail.fullShown ? 'none' : 360, overflow: detail.fullShown ? 'visible' : 'hidden' }}
                    >
                      {renderMarkdownNote(detail.content.content)}
                    </div>
                    {detail.content.content.split(/\s+/).length > 280 && (
                      <button className={studentModalCss.spNoteExpand} onClick={() => setDetail(current => ({ ...current, fullShown: !current.fullShown }))}>
                        {detail.fullShown ? '▲ Show Less' : '▼ Expand Full Note'}
                      </button>
                    )}
                  </>
                ) : (
                  <p style={{ color: '#607d8b' }}>The student has not generated notes for this task yet.</p>
                )}
              </div>
            ) : detail.loading ? (
              <div className={studentModalCss.spVideoWrap}><div className={studentModalCss.spVideoEmpty}>Loading video…</div></div>
            ) : detail.error || !detail.content ? (
              <div className={studentModalCss.spVideoWrap}><div className={studentModalCss.spVideoEmpty}>{detail.error || 'No video found for this topic.'}</div></div>
            ) : (
              <>
                <div className={studentModalCss.spVideoWrap}>
                  <iframe
                    title={detail.content.title || detail.task?.title}
                    src={`https://www.youtube.com/embed/${detail.content.videoId}?rel=0`}
                    className={studentModalCss.spVideoIframe}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                </div>
                <p className={studentModalCss.spModalHint}>{detail.content.title}{detail.content.channelTitle ? ` · ${detail.content.channelTitle}` : ''}</p>
              </>
            )}
          </div>
        </div>
      )}

      {detail.type === 'mock' && (
        <div className={teacherModalCss.tModalOverlay} onClick={() => setDetail({ type: null, task: null, loading: false, content: null, error: '', fullShown: false })}>
          <div className={`${teacherModalCss.tModalBox} BubblePopIn`} onClick={event => event.stopPropagation()}>
            <div className={teacherModalCss.tModalHeader}>
              <div>
                <span className={teacherModalCss.tModalBadge}>🎯 Practice Mock</span>
                <h3 className={teacherModalCss.tModalTitle}>{detail.task?.title}</h3>
              </div>
              <button className={teacherModalCss.tModalClose} onClick={() => setDetail({ type: null, task: null, loading: false, content: null, error: '', fullShown: false })}>✕</button>
            </div>
            <div className={teacherModalCss.tModalBody}>
              {detail.loading && <div className={teacherModalCss.tModalLoading}><span className={teacherModalCss.tModalSpinner} /><p>Loading…</p></div>}
              {!detail.loading && detail.error && <p className={teacherModalCss.tModalError}>{detail.error}</p>}
              {!detail.loading && !detail.error && (detail.content ? (
                <div>
                  <div className={teacherModalCss.tModalScoreCard} style={{ borderColor: detail.content.passed ? '#a5d6a7' : '#ef9a9a', background: detail.content.passed ? 'linear-gradient(135deg,#e8f5e9,#f1f8e9)' : 'linear-gradient(135deg,#fce4ec,#fef9fa)' }}>
                    <span className={teacherModalCss.tModalScorePct} style={{ color: detail.content.passed ? '#2e7d32' : '#c62828' }}>{detail.content.pct}%</span>
                    <span className={teacherModalCss.tModalScoreSub}>{detail.content.score}/{detail.content.total} correct · {detail.content.passed ? 'Pass' : 'Needs Work'}</span>
                    {detail.content.timeTaken > 0 && <span className={teacherModalCss.tModalScoreSub}>Time: {Math.floor(detail.content.timeTaken / 60)}m {detail.content.timeTaken % 60}s</span>}
                  </div>
                  {(detail.content.questions || []).length > 0 && (
                    <div className={teacherModalCss.tModalQList}>
                      {detail.content.questions.map((question, index) => (
                        <div key={index} className={`${teacherModalCss.tModalQItem} ${question.isCorrect ? teacherModalCss.tModalQCorrect : teacherModalCss.tModalQWrong}`}>
                          <div className={teacherModalCss.tModalQHeader}>
                            <span className={teacherModalCss.tModalQBadge}>{question.isCorrect ? '✓' : '✗'}</span>
                            <p className={teacherModalCss.tModalQText}><strong>Q{index + 1}.</strong> {question.questionText}</p>
                          </div>
                          <div className={teacherModalCss.tModalQAnswers}>
                            {!question.isCorrect && question.chosen && <span className={teacherModalCss.tModalQYours}>Student: {question.chosen}</span>}
                            <span className={teacherModalCss.tModalQCorrect2}>Correct: {question.correct}</span>
                          </div>
                          {question.explanation && <p className={teacherModalCss.tModalQExplanation}>{question.explanation}</p>}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className={teacherModalCss.tModalEmpty}>{detail.task?.done ? 'Practice was completed but no result data is available (may have been done before tracking was enabled).' : 'This student has not completed the practice mock for this task yet.'}</p>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const VIOLATION_META = {
  tab_switch:      { label: 'Tab Switch',      color: '#f57c00', bg: '#fff3e0' },
  window_blur:     { label: 'Window Blur',     color: '#f57c00', bg: '#fff3e0' },
  copy_attempt:    { label: 'Copy Attempt',    color: '#c62828', bg: '#fce4ec' },
  paste_attempt:   { label: 'Paste Attempt',   color: '#c62828', bg: '#fce4ec' },
  dev_tools:       { label: 'Dev Tools',       color: '#c62828', bg: '#fce4ec' },
  right_click:     { label: 'Right Click',     color: '#607d8b', bg: '#eceff1' },
  fullscreen_exit: { label: 'Fullscreen Exit', color: '#c62828', bg: '#fce4ec' },
  mouse_leave:     { label: 'Mouse Left',      color: '#607d8b', bg: '#eceff1' },
  auto_submit:     { label: 'Auto-Submitted',  color: '#c62828', bg: '#fce4ec' },
};

// ─── Read-only exam integrity/violations report for parents ──────────────────
function ChildIntegrityReport({ proctoring }) {
  const [screenshotModal, setScreenshotModal] = useState(null);
  if (proctoring?.locked) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'linear-gradient(135deg, #fff3e0, #ffe0b2)', border: '1px solid #ffcc80', color: '#a15c00', borderRadius: 10, padding: '14px 18px', fontSize: '0.88rem', fontWeight: 600 }}>
        🔒 {proctoring.message || 'Locked by Admin'}
      </div>
    );
  }
  if (!proctoring) {
    return (
      <p style={{ color: '#90a4ae', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
        No integrity/proctoring data was recorded for this attempt.
      </p>
    );
  }
  const p = proctoring;
  const countRows = [
    { key: 'tab_switch',      value: p.tabSwitchCount      },
    { key: 'window_blur',     value: p.windowBlurCount     },
    { key: 'copy_attempt',    value: p.copyAttemptCount    },
    { key: 'paste_attempt',   value: p.pasteAttemptCount   },
    { key: 'dev_tools',       value: p.devToolsCount       },
    { key: 'right_click',     value: p.rightClickCount     },
    { key: 'fullscreen_exit', value: p.fullscreenExitCount },
    { key: 'mouse_leave',     value: p.mouseLeaveCount     },
  ].filter(r => r.value > 0);
  const totalViolations = countRows.reduce((s, r) => s + r.value, 0);
  const violations = Array.isArray(p.violations) ? p.violations : [];
  const riskBand =
    (p.riskScore || 0) >= 40 ? { label: 'High Risk',   color: '#c62828', bg: '#fce4ec' } :
    (p.riskScore || 0) >= 20 ? { label: 'Medium Risk', color: '#f57c00', bg: '#fff3e0' } :
                                { label: 'Low Risk',    color: '#2e7d32', bg: '#e8f5e9' };

  return (
    <div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div style={{ flex: '1 1 140px', padding: '12px 14px', borderRadius: 12, background: riskBand.bg, borderTop: `3px solid ${riskBand.color}` }}>
          <div style={{ fontSize: '0.72rem', color: '#607d8b', fontWeight: 600 }}>Risk Score</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: riskBand.color }}>
            {p.riskScore ?? 0}<span style={{ fontSize: '0.75rem', fontWeight: 500 }}>/100</span>
          </div>
          <div style={{ fontSize: '0.7rem', fontWeight: 700, color: riskBand.color }}>{riskBand.label}</div>
        </div>
        <div style={{ flex: '1 1 140px', padding: '12px 14px', borderRadius: 12, background: '#f8fafc' }}>
          <div style={{ fontSize: '0.72rem', color: '#607d8b', fontWeight: 600 }}>Total Events</div>
          <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#0c1e28' }}>{totalViolations}</div>
        </div>
        {p.flaggedForReview && (
          <div style={{ flex: '1 1 140px', padding: '12px 14px', borderRadius: 12, background: '#fce4ec' }}>
            <div style={{ fontSize: '0.72rem', color: '#c62828', fontWeight: 700 }}>⚑ Flagged for Review</div>
          </div>
        )}
      </div>

      {countRows.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          {countRows.map(({ key, value }) => {
            const meta = VIOLATION_META[key] || { label: key, color: '#555', bg: '#f5f5f5' };
            return (
              <span key={key} style={{ padding: '4px 10px', borderRadius: 999, fontSize: '0.75rem', fontWeight: 700, color: meta.color, background: meta.bg }}>
                {meta.label} ×{value}
              </span>
            );
          })}
        </div>
      )}

      {violations.length > 0 ? (
        <div className={css.tableWrap}>
          <table className={css.table}>
            <thead>
              <tr><th>#</th><th>Event</th><th>Time</th><th>Detail</th><th>Screenshot</th></tr>
            </thead>
            <tbody>
              {violations.map((v, i) => {
                const meta = VIOLATION_META[v.type] || { label: v.type, color: '#555', bg: '#f5f5f5' };
                return (
                  <tr key={i}>
                    <td>{i + 1}</td>
                    <td><span style={{ color: meta.color, fontWeight: 600 }}>{meta.label}</span></td>
                    <td>{v.timestamp ? new Date(v.timestamp).toLocaleTimeString('en-NG') : '—'}</td>
                    <td>{v.detail || '—'}</td>
                    <td>
                      {v.screenshotUrl ? (
                        <button type="button" onClick={() => setScreenshotModal(v.screenshotUrl)} style={{ color: '#5fbec5', fontSize: '0.75rem', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}>📷 View</button>
                      ) : (
                        <span style={{ color: '#90a4ae', fontSize: '0.75rem' }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p style={{ color: '#90a4ae', fontSize: '0.85rem' }}>No individual violation events were recorded.</p>
      )}

      {screenshotModal && (
        <div className={teacherModalCss.tModalOverlay} onClick={() => setScreenshotModal(null)}>
          <div className={`${teacherModalCss.tModalBox} BubblePopIn`} onClick={e => e.stopPropagation()}>
            <div className={teacherModalCss.tModalHeader}>
              <div>
                <span className={teacherModalCss.tModalBadge}>📷 Screenshot</span>
                <h3 className={teacherModalCss.tModalTitle}>Violation Screenshot</h3>
              </div>
              <button className={teacherModalCss.tModalClose} onClick={() => setScreenshotModal(null)}>✕</button>
            </div>
            <div className={teacherModalCss.tModalBody}>
              <div style={{ textAlign: 'center' }}>
                <div
                  id="violation-screenshot-wrap-parent"
                  style={{ borderRadius: 8, overflow: 'hidden', border: '1px solid #e0eaec', marginBottom: 12 }}
                >
                  <img
                    src={screenshotModal}
                    alt="Violation screenshot"
                    style={{ maxWidth: '100%', display: 'block' }}
                  />
                </div>
                <button
                  className={teacherModalCss.primaryBtn}
                  style={{ fontSize: '0.82rem' }}
                  onClick={() => {
                    const wrap = document.getElementById('violation-screenshot-wrap-parent');
                    if (wrap?.requestFullscreen) wrap.requestFullscreen();
                  }}
                >
                  ⛶ Fullscreen
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Inline panel: fetches the graded result for one exam on demand ──────────
function ChildResultPanel({ exam, fetchFn, membershipId }) {
  const [tab, setTab] = useState('grading'); // grading | studyplan | violations
  const [grading, setGrading] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subjectBreakdown, setSubjectBreakdown] = useState([]);
  const [activeSubjectIdx, setActiveSubjectIdx] = useState('all');
  const [proctoring, setProctoring] = useState(null);

  const [studyPlan, setStudyPlan] = useState(null);
  const [studyProgress, setStudyProgress] = useState(null);
  const [studyLoading, setStudyLoading] = useState(false);
  const [studyLoaded, setStudyLoaded] = useState(false);

  useEffect(() => {
    if (!exam?.schoolExamId) return;
    setLoading(true);
    setTab('grading');
    setActiveSubjectIdx('all');
    setStudyLoaded(false);
    setStudyPlan(null);
    fetchFn(`/parent/children/${membershipId}/exams/${exam.schoolExamId}/result`)
      .then(r => r.json())
      .then(d => {
        setGrading(d.result?.grading || null);
        setSubjectBreakdown(d.result?.subjectBreakdown || []);
        setProctoring(d.result?.proctoring || null);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [exam?.schoolExamId, membershipId, fetchFn]);

  useEffect(() => {
    if (tab !== 'studyplan' || studyLoaded || !exam?.schoolExamId) return;
    setStudyLoading(true);
    fetchFn(`/parent/children/${membershipId}/exams/${exam.schoolExamId}/study-plan`)
      .then(r => r.json())
      .then(d => {
        setStudyPlan(d.studyPlan || null);
        setStudyProgress(d.progress || null);
        setStudyLoaded(true);
        setStudyLoading(false);
      })
      .catch(() => { setStudyLoaded(true); setStudyLoading(false); });
  }, [tab, studyLoaded, exam?.schoolExamId, membershipId, fetchFn]);

  return (
    <div style={{ padding: '16px 24px 24px', borderTop: '2px solid #5fbec5' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        {[
          { k: 'grading', l: 'Grading' },
          { k: 'studyplan', l: 'Study Plan' },
          { k: 'violations', l: 'Exam Integrity' },
        ].map(({ k, l }) => (
          <button
            key={k}
            type="button"
            onClick={() => setTab(k)}
            style={{
              padding: '6px 14px', borderRadius: 999, fontSize: '0.78rem', fontWeight: 700,
              cursor: 'pointer',
              border: tab === k ? '1px solid #3e8f93' : '1px solid #d0dce0',
              background: tab === k ? '#3e8f93' : '#fff',
              color: tab === k ? '#fff' : '#455a64',
            }}
          >
            {l}
          </button>
        ))}
      </div>

      {tab === 'grading' && (
        loading ? (
          <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className={css.skel} style={{ width: '100%', height: 80 }} />
          </div>
        ) : grading ? (
          <ChildGradingDetails
            grading={grading}
            exam={exam}
            isMultiSubject={exam?.isMultiSubject}
            subjectBreakdown={subjectBreakdown}
            activeSubjectIdx={activeSubjectIdx}
            setActiveSubjectIdx={setActiveSubjectIdx}
          />
        ) : (
          <p style={{ color: '#90a4ae', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
            Detailed grading insight is not available for this exam yet.
          </p>
        )
      )}

      {tab === 'studyplan' && (
        studyLoading ? (
          <div style={{ height: 120, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className={css.skel} style={{ width: '100%', height: 80 }} />
          </div>
        ) : !studyPlan ? (
          <p style={{ color: '#90a4ae', fontSize: '0.85rem', textAlign: 'center', padding: '1rem' }}>
            A study plan has not been generated for this exam yet.
          </p>
        ) : (
          <ChildStudyPlanView
            studyPlan={studyPlan}
            progress={studyProgress}
            exam={exam}
            membershipId={membershipId}
            fetchFn={fetchFn}
          />
        )
      )}

      {tab === 'violations' && <ChildIntegrityReport proctoring={proctoring} />}
    </div>
  );
}

// ─── Main segment: a single child's full exam list + performance summary ─────
export default function ChildExamPerformance({ membershipId, student, fetchFn, onBack, onStudentUpdated }) {
  const [progress, setProgress]     = useState(null);
  const [exams, setExams]           = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selected, setSelected]     = useState(null);
  const [filterTerm, setFilterTerm] = useState('');
  const [filterSubject, setFilterSubject] = useState('');
  const [careerPath, setCareerPath] = useState(null);
  const [careerPathLoading, setCareerPathLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoError, setPhotoError] = useState('');
  const [photoSuccess, setPhotoSuccess] = useState('');
  const photoInputRef = useRef(null);

  useEffect(() => {
    return () => {
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  useEffect(() => {
    if (!membershipId) return;
    let alive = true;
    setCareerPathLoading(true);
    fetchFn(`/parent/children/${membershipId}/career-path`).then(r => r.json()).then(d => {
      if (!alive) return;
      setCareerPath(d);
      setCareerPathLoading(false);
    }).catch(() => { if (alive) setCareerPathLoading(false); });
    return () => { alive = false; };
  }, [membershipId, fetchFn]);

  const handlePhotoChange = async event => {
    const file = event.target.files?.[0];
    if (photoInputRef.current) photoInputRef.current.value = '';
    if (!file) return;

    setPhotoError('');
    setPhotoSuccess('');
    const validation = validateImageFile(file, 2);
    if (!validation.valid) {
      setPhotoError(validation.error);
      return;
    }

    let finalFile = file;
    if (file.size > 2 * 1024 * 1024) {
      try {
        const result = await compressImageIfNeeded(file, 2);
        finalFile = result.file;
      } catch {
        setPhotoError('Image compression failed. Try a smaller image.');
        return;
      }
    }

    if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
    setSelectedFile(finalFile);
    setPreviewUrl(URL.createObjectURL(finalFile));
  };

  const uploadChildPhoto = async () => {
    if (!selectedFile) return;
    setUploadingPhoto(true);
    setPhotoError('');
    setPhotoSuccess('');
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      const res = await fetchFn(`/parent/children/${membershipId}/profile-image`, { method: 'POST', body: formData });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.profileImg) {
        setPhotoError(data.error || 'Upload failed. Please try again.');
        return;
      }
      if (onStudentUpdated) onStudentUpdated({ ...student, profileImg: data.profileImg });
      setSelectedFile(null);
      if (previewUrl && previewUrl.startsWith('blob:')) URL.revokeObjectURL(previewUrl);
      setPreviewUrl('');
      setPhotoSuccess('Passport image updated.');
    } catch {
      setPhotoError('Connection error. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  useEffect(() => {
    if (!membershipId) return;
    let alive = true;
    setLoading(true);
    Promise.all([
      fetchFn(`/parent/children/${membershipId}/progress`).then(r => r.json()),
      fetchFn(`/parent/children/${membershipId}/exams?limit=100`).then(r => r.json()),
    ]).then(([prog, ex]) => {
      if (!alive) return;
      setProgress(prog.progress || null);
      const completed = (ex.exams || []).filter(e => e.status === 'completed');
      setExams(completed);
      setLoading(false);
    }).catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [membershipId, fetchFn]);

  const chartData = exams
    .filter(e => e.percentage != null && e.resultVisibility === 'immediate')
    .sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt))
    .map(e => ({ name: fmtDateShort(e.submittedAt), score: e.percentage }));

  const termOptions = Array.from(
    new Map(exams.filter(e => e.term).map(e => [`${e.term}||${e.session}`, { term: e.term, session: e.session }])).values()
  ).sort((a, b) => {
    const order = { first: 0, second: 1, third: 2 };
    return (order[a.term] ?? 9) - (order[b.term] ?? 9);
  });

  const subjectOptions = Array.from(new Set(exams.map(e => e.subjectName).filter(Boolean))).sort();

  const filteredExams = exams.filter(e => {
    if (filterTerm) {
      const combined = `${e.term}||${e.session}`;
      if (combined !== filterTerm && e.term !== filterTerm) return false;
    }
    if (filterSubject && e.subjectName !== filterSubject) return false;
    return true;
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18 }}>
        <button
          onClick={onBack}
          style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#3e8f93', fontWeight: 700, fontSize: '0.85rem', cursor: 'pointer', padding: '4px 0' }}
        >
          <ArrowBackIcon style={{ fontSize: '1.1rem' }} /> Back to My Children
        </button>
      </div>

      <div className={css.cardHeader} style={{ marginBottom: 12 }}>
        <h2 className={css.cardTitle}>{student?.firstName} {student?.lastName} — Exams & Performance</h2>
      </div>

      <div className={css.childPhotoCard}>
        <div className={css.childPhotoPreview}>
          {hasChildPhoto(previewUrl || student?.profileImg) ? (
            <img src={previewUrl || student.profileImg} alt={`${student?.firstName || 'Child'} passport`} />
          ) : (
            <span>{childInitials(student?.firstName, student?.lastName)}</span>
          )}
        </div>
        <div className={css.childPhotoContent}>
          <div className={css.childPhotoTitle}>Passport Photo</div>
          <p className={css.childPhotoHint}>Update {student?.firstName || 'your child'}'s photo for school records and parent views.</p>
          <input ref={photoInputRef} type="file" accept="image/*" className={css.hiddenFileInput} onChange={handlePhotoChange} />
          <div className={css.profilePhotoActions}>
            <button type="button" className={css.secondaryBtn} onClick={() => photoInputRef.current?.click()}>Choose Image</button>
            <button type="button" className={css.primaryBtn} onClick={uploadChildPhoto} disabled={uploadingPhoto || !selectedFile}>
              {uploadingPhoto ? <span>Uploading...</span> : 'Upload Passport'}
            </button>
          </div>
          {photoError && <div className={css.inlineError}>{photoError}</div>}
          {photoSuccess && <div className={css.inlineSuccess}>{photoSuccess}</div>}
        </div>
      </div>

      {/* Stat cards */}
      <div className={css.statsRow}>
        {loading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={css.statCard}>
              <div className={css.skel} style={{ height: 12, width: '60%', marginBottom: 10 }} />
              <div className={css.skel} style={{ height: 28, width: '40%' }} />
            </div>
          ))
        ) : (
          <>
            <div className={css.statCard}>
              <span className={css.statLabel}>Exams Completed</span>
              <span className={css.statValue}>{progress?.examsAttempted ?? 0}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Passed</span>
              <span className={css.statValue} style={{ color: '#10b981' }}>{progress?.examsPassed ?? 0}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Avg Score</span>
              <span className={css.statValue} style={{ color: '#0c7c84' }}>{progress?.avgPercentage != null ? `${progress.avgPercentage}%` : '—'}</span>
            </div>
            <div className={css.statCard}>
              <span className={css.statLabel}>Pass Rate</span>
              <span className={css.statValue}>
                {progress?.examsAttempted ? `${Math.round((progress.examsPassed / progress.examsAttempted) * 100)}%` : '—'}
              </span>
            </div>
          </>
        )}
      </div>

      {/* Performance chart */}
      {chartData.length > 1 && (
        <div className={css.card}>
          <h3 style={{ margin: '0 0 18px', fontSize: '0.92rem', color: '#0c1e28' }}>Score Over Time</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f4f8" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} />
              <Tooltip formatter={v => [`${v}%`, 'Score']} />
              <Line type="monotone" dataKey="score" stroke="#5fbec5" strokeWidth={2} dot={{ r: 4, fill: '#5fbec5' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Career Path */}
      <div className={css.card}>
        <h3 style={{ margin: '0 0 18px', fontSize: '0.92rem', color: '#0c1e28' }}>Career Path</h3>
        {careerPathLoading ? (
          <div className={css.skel} style={{ width: '100%', height: 120, borderRadius: 10 }} />
        ) : (
          <CareerPathSummaryCard
            enabled={careerPath?.enabled}
            status={careerPath?.status}
            eligibility={careerPath?.eligibility}
            snapshot={careerPath?.snapshot}
            lastComputedAt={careerPath?.lastComputedAt}
          />
        )}
      </div>

      {/* Subject breakdown */}
      {(progress?.subjectBreakdown || []).length > 0 && (
        <div className={css.card}>
          <h3 style={{ margin: '0 0 16px', fontSize: '0.92rem', color: '#0c1e28' }}>Subject Breakdown</h3>
          {progress.subjectBreakdown.map(s => (
            <div key={s.subject} style={{ marginBottom: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.83rem', marginBottom: 5 }}>
                <span style={{ fontWeight: 600, color: '#0c1e28' }}>{s.subject}</span>
                <span style={{ color: '#90a4ae' }}>{s.avgPercentage}%</span>
              </div>
              <div className={css.progressTrack}>
                <div className={css.progressFill} style={{ width: `${s.avgPercentage}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Weak topics */}
      {(progress?.weakTopics || []).length > 0 && (
        <div className={css.card}>
          <h3 style={{ margin: '0 0 12px', fontSize: '0.92rem', color: '#0c1e28' }}>Weak Topics</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {progress.weakTopics.map(t => (
              <span key={t.topic} className={`${css.chip} ${css.chipRed}`}>{t.topic} ×{t.count}</span>
            ))}
          </div>
        </div>
      )}

      <ReportCardLibrary
        fetchFn={fetchFn}
        listPath={`/parent/children/${membershipId}/report-cards`}
        downloadPath={card => `/parent/children/${membershipId}/report-card?term=${card.term}&session=${encodeURIComponent(card.session)}`}
        filenamePrefix={`report-card-${student?.lastName || 'student'}`}
        title={`${student?.firstName || 'Child'}'s Released Report Cards`}
      />

      <ReportCardLibrary
        fetchFn={fetchFn}
        listPath={`/parent/children/${membershipId}/mid-term-reports`}
        downloadPath={card => `/parent/children/${membershipId}/mid-term-report?term=${card.term}&session=${encodeURIComponent(card.session)}`}
        filenamePrefix={`mid-term-report-${student?.lastName || 'student'}`}
        title={`${student?.firstName || 'Child'}'s Released Mid-Term Results`}
        subtitle="Interim results published by the school during the term."
        emptyTitle="No released mid-term results yet"
        emptyDescription="Published mid-term results will appear here automatically."
        titleId="released-child-mid-term-reports-title"
      />

      <ChildCurriculumSegment membershipId={membershipId} student={student} fetchFn={fetchFn} />

      {/* Exam history */}
      <div className={css.card}>
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: '0.92rem', color: '#0c1e28', flex: 1 }}>Exam History</h3>

          <select
            value={filterTerm}
            onChange={e => { setFilterTerm(e.target.value); setSelected(null); }}
            style={{ padding: '5px 10px', border: '1px solid #d0dce0', borderRadius: 8, fontSize: '0.8rem' }}
          >
            <option value="">All Terms</option>
            {termOptions.map(o => (
              <option key={`${o.term}||${o.session}`} value={`${o.term}||${o.session}`}>
                {termLabel(o.term)} Term — {o.session}
              </option>
            ))}
          </select>

          {subjectOptions.length > 1 && (
            <select
              value={filterSubject}
              onChange={e => { setFilterSubject(e.target.value); setSelected(null); }}
              style={{ padding: '5px 10px', border: '1px solid #d0dce0', borderRadius: 8, fontSize: '0.8rem' }}
            >
              <option value="">All Subjects</option>
              {subjectOptions.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          )}

        </div>

        {loading ? (
          <div className={css.skel} style={{ height: 60, width: '100%' }} />
        ) : filteredExams.length === 0 ? (
          <div className={css.emptyState}>
            <BarChartIcon style={{ fontSize: '2rem', color: '#c0ccda' }} />
            <p className={css.emptyTitle}>No completed exams yet</p>
            <p className={css.emptyDesc}>Results will appear here once your child submits an exam.</p>
          </div>
        ) : (
          <div className={css.tableWrap}>
            <table className={css.table}>
              <thead>
                <tr>
                  <th>Exam</th><th>Subject</th><th>Score</th><th>Result</th><th>Date &amp; Time</th><th>Term</th><th>Session</th>
                </tr>
              </thead>
              <tbody>
                {filteredExams.map(e => {
                  const isOpen = selected?.schoolExamId === e.schoolExamId;
                  return (
                    <React.Fragment key={String(e.schoolExamId)}>
                      <tr
                        style={{ cursor: 'pointer', background: isOpen ? '#f0f9fa' : undefined, transition: 'background 0.15s' }}
                        onClick={() => setSelected(isOpen ? null : e)}
                      >
                        <td style={{ fontWeight: 600 }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.7rem', color: isOpen ? '#5fbec5' : '#b0bec5', transition: 'transform 0.2s', display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'none' }}>▶</span>
                            {e.title}
                            <RetakeBadge count={e.retakeCount} retake={e.retake} />
                          </span>
                        </td>
                        <td>
                          {e.isMultiSubject ? (
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: 999, fontSize: '0.72rem', fontWeight: 700,
                              background: 'rgba(95,190,197,0.14)', color: '#0f3135', border: '1px solid rgba(95,190,197,0.4)',
                            }}>
                              Multi-Subject
                            </span>
                          ) : (e.subjectName || '—')}
                        </td>
                        <td>{e.resultVisibility === 'immediate' && e.percentage != null ? `${e.percentage}%` : '—'}</td>
                        <td>
                          {e.resultVisibility === 'immediate'
                            ? <span className={`${css.chip} ${e.passed ? css.chipGreen : css.chipRed}`}>{e.passed ? 'Passed' : 'Failed'}</span>
                            : <span className={css.chip} style={{ background: '#f1f5f8', color: '#78909c', border: '1px solid #cfd8dc' }}>Pending</span>}
                        </td>
                        <td>{fmtDateTime(e.submittedAt)}</td>
                        <td>{termLabel(e.term)}</td>
                        <td>{e.session || '—'}</td>
                      </tr>
                      {isOpen && (
                        <tr>
                          <td colSpan={7} style={{ padding: 0, background: '#f8fafc' }}>
                            {e.resultVisibility === 'immediate' ? (
                              <ChildResultPanel exam={e} fetchFn={fetchFn} membershipId={membershipId} />
                            ) : (
                              <p style={{ padding: '16px 24px', color: '#90a4ae', fontSize: '0.85rem' }}>
                                Results for this exam have not been released yet.
                              </p>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
